import { z } from 'zod'
import type { ProgramSegment, TakeSpan } from './timeline'

export const transcriptSourceId = z.string().regex(/^[0-9a-f-]{36}:(camera|mic|mix|screen-\d{1,2})$/)
const ms = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER)
export const transcriptWord = z.tuple([
  ms, ms, z.string().normalize('NFC').trim().min(1).max(100).regex(/^[^\p{Cc}\p{Cf}\p{Zl}\p{Zp}]+$/u, 'Word contains invalid characters').regex(/\S/u, 'Word must be visible'),
  z.number().min(0).max(1).optional(),
])
export type TranscriptWord = z.infer<typeof transcriptWord>
const transcriptTake = z.object({ takeId: z.string().uuid(), words: z.array(transcriptWord).max(200_000) }).strict()
const putTranscriptFields = {
  language: z.string().min(1).max(35).regex(/^[A-Za-z]{2,8}(?:-[A-Za-z0-9]{1,8})*$/).refine((s) => { try { return Intl.getCanonicalLocales(s).length === 1 } catch { return false } }, 'Invalid BCP-47 language'),
  model: z.string().trim().min(1).max(100).regex(/^[^\p{Cc}\p{Cf}\p{Zl}\p{Zp}]+$/u, 'Model contains invalid characters'), origin: z.enum(['agent', 'asr', 'upload']).default('agent'),
}
export const putTranscriptBody = z.object({ ...putTranscriptFields, takes: z.array(transcriptTake).min(1).max(50) }).strict().superRefine((body, ctx) => {
  if (body.takes.reduce((n, t) => n + t.words.length, 0) > 200_000) ctx.addIssue({ code: 'custom', path: ['takes'], message: 'At most 200000 words' })
  for (const error of validateWords(body.takes)) ctx.addIssue({ code: 'custom', path: ['takes'], message: error.message, params: { index: error.index } })
})
export type PutTranscriptBody = z.infer<typeof putTranscriptBody>
/** Words unchecked, so `checkTranscriptBody` can stop after a few bad words instead of listing every one. */
const putTranscriptEnvelope = z.object({ ...putTranscriptFields,
  takes: z.array(z.object({ takeId: z.string().uuid(), words: z.array(z.unknown()).max(200_000) }).strict()).min(1).max(50) }).strict()
export type TranscriptIssue = { path: PropertyKey[]; message: string; index?: number }
export type TranscriptBodyCheck = { success: true; data: PutTranscriptBody } | { success: false; issues: TranscriptIssue[]; truncated: boolean }

/**
 * `putTranscriptBody` with a bounded error list: at most `limit` issues, `truncated` when more exist.
 * Stages run in order (fields and takes, then each word, then timing) and a failed stage ends the check,
 * so a malformed maximum-size body costs a few issues, not one or more per word.
 */
export function checkTranscriptBody(input: unknown, limit = 20): TranscriptBodyCheck {
  const issues: TranscriptIssue[] = []
  let truncated = false
  const add = (issue: TranscriptIssue) => { if (issues.length < limit) issues.push(issue); else truncated = true; return truncated }
  // More takes than allowed: check one past the limit, so a huge array costs at most 51 takes of issues.
  const takes = (input as { takes?: unknown } | null)?.takes
  const envelope = putTranscriptEnvelope.safeParse(Array.isArray(takes) && takes.length > 50 ? { ...(input as object), takes: takes.slice(0, 51) } : input)
  if (!envelope.success) {
    for (const issue of envelope.error.issues) if (add({ path: issue.path, message: issue.message })) break
    return { success: false, issues, truncated }
  }
  const total = envelope.data.takes.reduce((n, t) => n + t.words.length, 0)
  if (total > 200_000) return { success: false, issues: [{ path: ['takes'], message: 'At most 200000 words' }], truncated: false }
  let index = 0
  const parsed: PutTranscriptBody['takes'] = []
  for (const [t, take] of envelope.data.takes.entries()) {
    const words: TranscriptWord[] = []
    for (const [w, word] of take.words.entries()) {
      const result = transcriptWord.safeParse(word)
      if (result.success) words.push(result.data)
      else for (const issue of result.error.issues) if (add({ path: ['takes', t, 'words', w, ...issue.path], message: issue.message, index })) return { success: false, issues, truncated }
      index++
    }
    parsed.push({ takeId: take.takeId, words })
  }
  if (issues.length) return { success: false, issues, truncated }
  const errors = validateWords(parsed, undefined, limit + 1)
  if (errors.length) return { success: false, issues: errors.slice(0, limit).map((e) => ({ path: ['takes'], message: e.message, index: e.index })), truncated: errors.length > limit }
  return { success: true, data: { ...envelope.data, takes: parsed } }
}
export const transcriptMeta = z.object({
  sourceId: transcriptSourceId, person: z.string(), version: z.number().int().positive(),
  language: z.string(), model: z.string(), origin: z.enum(['agent', 'asr', 'upload']), wordCount: z.number().int().nonnegative(),
  createdAt: z.string(), createdBy: z.object({ name: z.string().nullable(), tokenLabel: z.string().nullable() }),
})
export type TranscriptMeta = z.infer<typeof transcriptMeta>
export const transcriptDoc = transcriptMeta.extend({ takes: z.array(transcriptTake.extend({ timelineStart: ms })), trust: z.literal('untrusted-speech') })
export type TranscriptDoc = z.infer<typeof transcriptDoc>
export const transcriptLine = z.object({ sourceId: z.string(), person: z.string(), t0: ms, t1: ms, text: z.string(), cut: z.enum(['none', 'partial', 'all']) })
export const transcriptScript = z.object({ trust: z.literal('untrusted-speech'), lines: z.array(transcriptLine) })
export const transcriptRange = z.object({ from: z.coerce.number().int().nonnegative().optional(), to: z.coerce.number().int().nonnegative().optional() }).strict()
export const transcriptScriptQuery = transcriptRange.extend({ sourceId: transcriptSourceId.optional() }).strict()
export const transcriptQuery = transcriptRange.extend({ version: z.coerce.number().int().positive().optional() }).strict()
export const transcriptParams = z.object({ id: z.string().uuid(), sourceId: transcriptSourceId }).strict()

/** Validate without mutating input. The writer clamps accepted overrun words afterwards. Stops after `limit` errors. */
export function validateWords(takes: { takeId: string; words: TranscriptWord[] }[], durations?: ReadonlyMap<string, number>, limit = Infinity): { index: number; message: string }[] {
  const errors: { index: number; message: string }[] = [], seen = new Set<string>()
  let index = 0
  for (const take of takes) {
    const fail = (message: string) => { if (errors.length < limit) errors.push({ index, message }) }
    if (seen.has(take.takeId)) fail('Take listed more than once')
    seen.add(take.takeId)
    const duration = durations?.get(take.takeId)
    if (durations && duration === undefined) fail('Unknown take')
    let previous = -1
    for (const [start, end] of take.words) {
      if (end < start) fail('Word end precedes start')
      if (start < previous) fail('Word starts must be non-decreasing')
      if (duration !== undefined && end > duration + 2000) fail('Word exceeds take duration')
      previous = start
      index++
      if (errors.length >= limit) return errors
    }
  }
  return errors
}

export function wordIsCut(segments: Pick<ProgramSegment, 'start' | 'end' | 'cut'>[], t: number): boolean {
  let lo = 0, hi = segments.length
  while (lo < hi) { const mid = (lo + hi) >>> 1; if (segments[mid]!.start <= t) lo = mid + 1; else hi = mid }
  const segment = segments[lo - 1]
  return !!segment && t < segment.end && segment.cut
}

/** Shared grouping for agent scripts and the editor. Each source/take is grouped independently. */
type LineOptions = { from?: number; to?: number; maxGapMs?: number; maxWords?: number }
export interface TimedTranscriptWord { sourceId: string; takeId: string; index: number; t0: number; t1: number; text: string; cut: boolean }
export interface TranscriptGroup { line: z.infer<typeof transcriptLine>; words: TimedTranscriptWord[] }

/** Keep word membership alongside each line for the panel, including overlaps and zero-length words. */
export function transcriptGroups(docs: TranscriptDoc[], spans: TakeSpan[], segments: ProgramSegment[], options: LineOptions = {}): TranscriptGroup[] {
  const groups: TranscriptGroup[] = []
  const starts = new Map(spans.map((s) => [s.takeId, s.start]))
  for (const doc of docs) for (const take of doc.takes) {
    let words: TimedTranscriptWord[] = []
    const flush = () => {
      if (!words.length) return
      const count = words.filter((w) => w.cut).length
      groups.push({ words, line: { sourceId: doc.sourceId, person: doc.person, t0: words[0]!.t0, t1: Math.max(...words.map((w) => w.t1)), text: words.map((w) => w.text).join(' '), cut: count === 0 ? 'none' : count === words.length ? 'all' : 'partial' } })
      words = []
    }
    const offset = starts.get(take.takeId) ?? take.timelineStart
    for (const [index, [start, end, text]] of take.words.entries()) {
      const t0 = offset + start, t1 = offset + end
      if (t1 < (options.from ?? 0) || t0 >= (options.to ?? Infinity)) continue
      if (words.length && (t0 - words[words.length - 1]!.t1 > (options.maxGapMs ?? 1000) || words.length >= (options.maxWords ?? 30))) flush()
      words.push({ sourceId: doc.sourceId, takeId: take.takeId, index, t0, t1, text, cut: wordIsCut(segments, (t0 + t1) / 2) })
    }
    flush()
  }
  return groups.sort((a, b) => a.line.t0 - b.line.t0 || a.line.sourceId.localeCompare(b.line.sourceId))
}

export function transcriptLines(docs: TranscriptDoc[], spans: TakeSpan[], segments: ProgramSegment[], options: LineOptions = {}): z.infer<typeof transcriptLine>[] {
  // Scripts need only line summaries. Retaining a timed object for every word
  // doubles the live working set of a maximum-size multi-source read.
  const lines: z.infer<typeof transcriptLine>[] = [], starts = new Map(spans.map((s) => [s.takeId, s.start]))
  for (const doc of docs) for (const take of doc.takes) {
    let text: string[] = [], first = 0, lastEnd = 0, maxEnd = 0, cuts = 0
    const flush = () => {
      if (!text.length) return
      lines.push({ sourceId: doc.sourceId, person: doc.person, t0: first, t1: maxEnd, text: text.join(' '), cut: !cuts ? 'none' : cuts === text.length ? 'all' : 'partial' })
      text = []; cuts = 0
    }
    const offset = starts.get(take.takeId) ?? take.timelineStart
    for (const [start, end, word] of take.words) {
      const t0 = offset + start, t1 = offset + end
      if (t1 < (options.from ?? 0) || t0 >= (options.to ?? Infinity)) continue
      if (text.length && (t0 - lastEnd > (options.maxGapMs ?? 1000) || text.length >= (options.maxWords ?? 30))) flush()
      if (!text.length) { first = t0; maxEnd = t1 }
      text.push(word); lastEnd = t1; maxEnd = Math.max(maxEnd, t1)
      if (wordIsCut(segments, (t0 + t1) / 2)) cuts++
    }
    flush()
  }
  return lines.sort((a, b) => a.t0 - b.t0 || a.sourceId.localeCompare(b.sourceId))
}
